<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    :title="`ルール${isEdit ? '編集' : '追加'}`"
    sub-title="NAT(IP Forwarding) インターネット → アクセス拠点"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- ID -->
                <v-row>
                  <v-col>
                    <template v-if="isEdit">
                      <app-label-group
                        label="ID"
                        :value="form.policyId"
                      ></app-label-group>
                    </template>
                    <template v-else>
                      <validation-provider
                        name="policyId"
                        v-model="form.policyId"
                        label="ID"
                        :rules="{
                          required: true,
                          numeric: true,
                          is_not_zero_padding: true,
                          between: { min: 1001, max: 9999 },
                          duplicate: { list: existsPolicyIds },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="ID"
                          :msg="$filter.message('info_policy_id')"
                          placement="end"
                        />
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </template>
                  </v-col>
                </v-row>
                <!-- 送信元 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <app-badge
                        item="送信元アドレス"
                        msg="アドレスを選択する場合は、１つ以上指定してください。"
                        placement="end"
                        :isRequired="radioSrcAddress === 'SELECT'"
                      ></app-badge>
                      <v-radio-group
                        v-model="radioSrcAddress"
                        density="compact"
                        hide-details
                      >
                        <v-radio value="ANY" label="any" />
                        <v-radio value="SELECT" label="アドレス" />
                      </v-radio-group>
                      <template v-if="radioSrcAddress === 'SELECT'">
                        <!-- 送信元アドレス選択 -->
                        <v-row align="start">
                          <v-col>
                            <validation-provider
                              v-model="form.srcAddressList"
                              name="srcAddressList"
                              label="送信元アドレス"
                              :rules="{ required: true }"
                              v-slot="{ componentField, errorMessage }"
                            >
                              <app-multi-select
                                v-bind="componentField"
                                :options="srcAddresses"
                                :multiple="true"
                                :custom-label="addressLabel"
                                track-by="ipAddressId"
                                :sort-orders="[
                                  ['ipAddressName', 'asc'],
                                  ['ipAddress', 'asc'],
                                ]"
                                group-label="label"
                                group-values="values"
                                :closeOnSelect="false"
                                :group-select="true"
                                selectGroupLabel="全てを選択します"
                                deselectGroupLabel="全ての選択を取り消します"
                                :error-messages="errorMessage"
                              >
                                <template #tag="{ option, remove }">
                                  <multi-select-tag
                                    :option="option"
                                    :remove="remove"
                                  >
                                    <div class="multiselect-tbody">
                                      <p class="v-col-3 pa-0">
                                        {{ option.ipAddress }}
                                      </p>
                                      <p class="v-col-9 pa-0">
                                        {{
                                          $filter.emptyTo(
                                            option.ipAddressName,
                                            "-"
                                          )
                                        }}
                                      </p>
                                    </div>
                                  </multi-select-tag>
                                </template>
                                <template #option="{ option }">
                                  <template v-if="option.$isLabel">{{
                                    option.$groupLabel
                                  }}</template>
                                  <div v-else class="multiselect-tbody">
                                    <p class="v-col-3 pa-0">
                                      {{ option.ipAddress }}
                                    </p>
                                    <p class="v-col-8 pa-0">
                                      {{
                                        $filter.emptyTo(
                                          option.ipAddressName,
                                          "-"
                                        )
                                      }}
                                    </p>
                                  </div>
                                </template>
                              </app-multi-select>
                            </validation-provider>
                          </v-col>
                          <v-btn
                            icon="$mdiCogOutline"
                            variant="text"
                            @click="showInternetSiteEdit"
                          ></v-btn>
                        </v-row>
                      </template>
                    </v-sheet>
                  </v-col>
                </v-row>
                <!-- IPマッピング -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <validation-provider
                        name="ipMapping"
                        v-model="form.ipMapping"
                        label="IPマッピング"
                        :rules="{ required: true }"
                        v-slot="{ componentField, errorMessage }"
                      >
                        <app-badge
                          item="IPマッピング"
                          :isInfo="false"
                          :isRequired="true"
                        />
                        <app-multi-select
                          v-bind="componentField"
                          :options="ipMappings"
                          :multiple="false"
                          :custom-label="ipMappingLabel"
                          track-by="virtualIpAddressSeq"
                          :error-messages="errorMessage"
                        >
                          <template #singleLabel="{ option }">
                            <div class="multiselect-tbody">
                              <p class="v-col-6 pa-0">
                                {{ option.ipMappingName }}
                              </p>
                              <p>
                                {{ $filter.emptyTo(option.description, "-") }}
                              </p>
                            </div>
                          </template>
                          <template #option="{ option }">
                            <div class="multiselect-tbody">
                              <p class="v-col-6 pa-0">
                                {{ option.ipMappingName }}
                              </p>
                              <p class="v-col-5 pa-0">
                                {{ $filter.emptyTo(option.description, "-") }}
                              </p>
                            </div>
                          </template>
                        </app-multi-select>
                      </validation-provider>
                    </v-sheet>
                  </v-col>
                </v-row>
                <!-- 有効/無効 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <v-row>
                        <v-col cols="6">
                          <v-row>
                            <v-col cols="4">
                              <app-badge
                                item="有効/無効"
                                :isRequired="false"
                                :is-info="false"
                              />
                            </v-col>
                            <v-col>
                              <v-radio-group
                                v-model="form.isPolicyStatus"
                                density="comfortable"
                                hide-details
                              >
                                <v-radio :value="true" label="有効" />
                                <v-radio :value="false" label="無効" />
                              </v-radio-group>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-sheet>
                  </v-col>
                </v-row>
                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <v-sheet border class="pa-4" rounded="sm">
                      <validation-provider
                        name="description"
                        v-model="form.description"
                        label="備考"
                        :rules="{ max: { length: 20 } }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="備考"
                          msg="20文字以内"
                          :isRequired="false"
                        />
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </v-sheet>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn variant="flat" type="submit" color="primary">保存</v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel"
                  >キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { GetPolicyType4 } from "@/apis/NwPolicyApi";
import type { Type4NatPolicy } from "@/modals/applianceContractSetting/type4/policy/ipForwarding/InternetType4PolicyRuleDestNatViewRD.vue";
import cloneDeep from "lodash/cloneDeep";
import type {
  Type4InternetAddress,
  Type4InternetSite,
} from "@/apis/Type4SiteApi";
import type { IpMappings } from "@/apis/IpMappingApi";
import MultiSelectTag from "@/components/MultiSelectTag.vue";
import InternetType4PolicyRuleDestNatConfirm from "@/modals/applianceContractSetting/type4/policy/ipForwarding/InternetType4PolicyRuleDestNatConfirm.vue";
import { VueFinalModal } from "vue-final-modal";
import Type4InternetSiteModify from "@/modals/type4/Type4InternetSiteModify.vue";

export default defineComponent({
  name: "InternetType4PolicyRuleDestNatEdit",
  components: { MultiSelectTag },
  props: {
    /** Type4Id. 主キー */
    type4Id: {
      type: String as PropType<string>,
      required: true,
    },
    /** 変更対象ポリシー */
    policy: {
      type: Object as PropType<Type4NatPolicy>,
      default: undefined,
    },
    /** type4ポリシー全体 */
    type4Policy: {
      type: Object as PropType<GetPolicyType4>,
      required: true,
    },
  },
  data() {
    const clonePolicy = cloneDeep(this.policy);
    const form = (clonePolicy ?? {
      srcAddressList: [],
      ipMapping: null,
      isPolicyStatus: true,
    }) as Type4NatPolicy;

    return {
      form,
      // 送信元、宛先（any/アドレス）
      radioSrcAddress: (form.srcAddressList.length === 0 ? "ANY" : "SELECT") as
        | "ANY"
        | "SELECT",
      // インターネット（アドレス一覧）
      internetAddresses: [] as Type4InternetSite["addressList"],
      // IPマッピング一覧
      ipMappings: [] as IpMappings["ipMappingList"],

      isLoaded: false,
    };
  },
  computed: {
    /** true: 編集, false: 新規 */
    isEdit(): boolean {
      return !!this.policy;
    },
    /** 送信元アドレスのSelectの選択肢 */
    srcAddresses(): {
      label: string;
      values: Type4InternetSite["addressList"];
    }[] {
      return this.internetAddresses.length !== 0
        ? [
            {
              label: "全選択",
              values: this.internetAddresses,
            },
          ]
        : [];
    },
    /** 既存のtype4ポリシーID一覧 */
    existsPolicyIds(): string[] {
      const p = this.type4Policy;
      return [
        ...(p.policyUtmType4?.accessPointToInternetPolicyList ?? []),
        ...(p.policyUtmType4?.internetToAccessPointPolicyList ?? []),
        ...(p.policyIfwType4?.accessPointToInternetPolicyList ?? []),
        ...(p.policyIfwType4?.internetToAccessPointPolicyList ?? []),
        ...(p.policyIPMasqueradeType4?.policyList ?? []),
        ...(p.policyNatType4?.policyList ?? []),
      ].map((e) => e.policyId);
    },
  },
  async mounted() {
    // 選択肢などの必要情報を取得
    this.internetAddresses = (
      await this.$api.type4SiteApi.getType4InternetSite(this.type4Id)
    ).addressList.sortBy(["ipAddress", "asc"]);
    this.ipMappings = (
      await this.$api.ipMapping.getType4IpMappingList(this.type4Id)
    ).ipMappingList.sortBy(["ipMappingName", "asc"]);

    this.isLoaded = true;
  },
  methods: {
    /** ルール編集の完了 */
    async submit() {
      const policy = {
        ...this.form,
        srcAddressList:
          this.radioSrcAddress === "ANY" ? [] : this.form.srcAddressList,
      } as Type4NatPolicy;

      await this.$modal.show(InternetType4PolicyRuleDestNatConfirm, { policy });
      (
        this.$refs.modal as typeof VueFinalModal & {
          ok: (entity: Type4NatPolicy) => void;
        }
      ).ok(policy);
    },
    /** アドレス選択肢ラベル名 */
    addressLabel({ ipAddressName, ipAddress }: Type4InternetAddress): string {
      return `${ipAddressName}  ${ipAddress}`;
    },
    /** IPマッピングラベル名（検索用） */
    ipMappingLabel({
      ipMappingName,
      description,
    }: IpMappings["ipMappingList"][0]): string {
      return `${ipMappingName} ${description}`;
    },
    /** インターネットサイト編集 */
    async showInternetSiteEdit() {
      let internetSite = await this.$api.type4SiteApi.getType4InternetSite(
        this.type4Id
      );
      await this.$modal.show(Type4InternetSiteModify, {
        internetSite: internetSite,
        type4Id: this.type4Id,
      });
      // インターネットアドレスリスト更新
      this.isLoaded = false;
      internetSite = await this.$api.type4SiteApi.getType4InternetSite(
        this.type4Id
      );
      this.internetAddresses = [
        ...internetSite.addressList.sortBy(["ipAddress", "asc"]),
      ];
      this.isLoaded = true;
    },
  },
});
</script>

<style scoped lang="scss">
:deep(.multiselect__tag) {
  display: block;
}
</style>
